/* Grace is Green — theme behaviour: drawers, accordions, sticky header, countdowns.
   Vanilla, no framework. Loaded deferred. */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     Body scroll lock that survives iOS Safari.
     --------------------------------------------------------------------- */
  var scrollY = 0;
  var lockCount = 0;

  function lockScroll() {
    if (lockCount++ > 0) return;
    scrollY = window.scrollY;
    document.body.style.top = -scrollY + 'px';
    document.body.classList.add('gg-scroll-locked');
  }

  function unlockScroll() {
    if (--lockCount > 0) return;
    lockCount = 0;
    document.body.classList.remove('gg-scroll-locked');
    document.body.style.top = '';
    window.scrollTo(0, scrollY);
  }

  /* ---------------------------------------------------------------------
     Focus trap
     --------------------------------------------------------------------- */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    var items = Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) { return el.offsetParent !== null; }
    );
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------------------------------------------------------------------
     Drawers — [data-gg-drawer-open="id"] opens, [data-gg-drawer-close] closes.
     --------------------------------------------------------------------- */
  var openDrawer = null;
  var lastTrigger = null;

  function drawerOpen(drawer, trigger) {
    if (!drawer || openDrawer === drawer) return;
    lastTrigger = trigger || document.activeElement;
    drawer.hidden = false;
    // Next frame, so the transition runs.
    requestAnimationFrame(function () { drawer.classList.add('is-open'); });
    lockScroll();
    openDrawer = drawer;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    var panel = drawer.querySelector('[role="dialog"]') || drawer;
    var focusable = panel.querySelector(FOCUSABLE);
    if (focusable) focusable.focus();
    document.dispatchEvent(new CustomEvent('gg:drawer:open', { detail: { drawer: drawer } }));
  }

  function drawerClose(drawer) {
    drawer = drawer || openDrawer;
    if (!drawer) return;
    drawer.classList.remove('is-open');
    var done = function () { drawer.hidden = true; };
    var panel = drawer.querySelector('.gg-drawer__panel');
    if (panel && window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      panel.addEventListener('transitionend', done, { once: true });
      // Safety net if transitionend never fires.
      setTimeout(done, 400);
    } else {
      done();
    }
    unlockScroll();
    openDrawer = null;
    document.querySelectorAll('[data-gg-drawer-open][aria-expanded="true"]').forEach(function (el) {
      el.setAttribute('aria-expanded', 'false');
    });
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    lastTrigger = null;
    document.dispatchEvent(new CustomEvent('gg:drawer:close', { detail: { drawer: drawer } }));
  }

  window.ggDrawer = { open: drawerOpen, close: drawerClose };

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-gg-drawer-open]');
    if (opener) {
      var drawer = document.getElementById(opener.getAttribute('data-gg-drawer-open'));
      if (drawer) {
        event.preventDefault();
        drawerOpen(drawer, opener);
      }
      return;
    }
    if (event.target.closest('[data-gg-drawer-close]')) {
      event.preventDefault();
      drawerClose();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (!openDrawer) return;
    if (event.key === 'Escape') {
      drawerClose();
    } else if (event.key === 'Tab') {
      var panel = openDrawer.querySelector('[role="dialog"]') || openDrawer;
      trapFocus(panel, event);
    }
  });

  /* ---------------------------------------------------------------------
     Accordions — [data-gg-acc-btn] toggles its [data-gg-acc-panel]: the one
     named in aria-controls, else the next sibling panel. aria-controls comes
     first because product-page and FAQ buttons sit inside a heading, where
     the panel is not their sibling.
     --------------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-gg-acc-btn]');
    if (!btn) return;
    var controls = btn.getAttribute('aria-controls');
    var panel = controls ? document.getElementById(controls) : null;
    if (panel && !panel.hasAttribute('data-gg-acc-panel')) panel = null;
    if (!panel) {
      panel = btn.nextElementSibling;
      while (panel && !panel.hasAttribute('data-gg-acc-panel')) panel = panel.nextElementSibling;
    }
    if (!panel) return;
    event.preventDefault();
    var isOpen = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!isOpen));
    panel.hidden = isOpen;
  });

  /* ---------------------------------------------------------------------
     Trustpilot TrustBox (snippets/gg-trustbox). Loads Trustpilot's bootstrap
     once, on any page that has a widget; widgets added later (the cart
     drawer re-renders its HTML) are filled by calling ggTrustbox(root).
     --------------------------------------------------------------------- */
  var TP_SRC = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
  window.ggTrustbox = function (root) {
    var els = (root || document).querySelectorAll('[data-gg-trustbox]');
    if (!els.length) return;
    if (window.Trustpilot && window.Trustpilot.loadFromElement) {
      els.forEach(function (el) {
        if (!el.querySelector('iframe')) window.Trustpilot.loadFromElement(el, true);
      });
      return;
    }
    var existing = document.querySelector('script[src="' + TP_SRC + '"]');
    if (existing) {
      // Loading (e.g. by the Trustpilot app): fill these once it is ready.
      existing.addEventListener('load', function () { window.ggTrustbox(root); }, { once: true });
      return;
    }
    // On load the bootstrap fills every .trustpilot-widget on the page.
    var s = document.createElement('script');
    s.src = TP_SRC;
    s.async = true;
    document.head.appendChild(s);
  };
  window.ggTrustbox();

  /* ---------------------------------------------------------------------
     Desktop two-pane mega menu — pointing at a subcategory on the left
     (hover or keyboard focus) shows its pane on the right.
     --------------------------------------------------------------------- */
  function megaActivate(cat) {
    var menu = cat.closest('.gg-mega2');
    if (!menu || cat.classList.contains('is-active')) return;
    var pane = document.getElementById(cat.getAttribute('data-gg-mega-cat'));
    if (!pane) return;
    menu.querySelectorAll('[data-gg-mega-cat].is-active, .gg-mega2__pane.is-active').forEach(function (el) {
      el.classList.remove('is-active');
    });
    cat.classList.add('is-active');
    pane.classList.add('is-active');
  }
  ['mouseover', 'focusin'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var cat = event.target.closest && event.target.closest('[data-gg-mega-cat]');
      if (cat) megaActivate(cat);
    });
  });

  /* ---------------------------------------------------------------------
     Phone menu — [data-gg-mnav-open="id"] slides that panel over the
     current one, [data-gg-mnav-back] returns to its data-parent. Inactive
     panels are inert. Every time the drawer opens it starts on the root.
     --------------------------------------------------------------------- */
  function mnavActivate(panel, from, back) {
    if (!panel) return;
    if (from) {
      from.classList.remove('is-active');
      if (back) from.classList.remove('is-behind'); else from.classList.add('is-behind');
      from.inert = true;
    }
    panel.classList.remove('is-behind');
    panel.classList.add('is-active');
    panel.inert = false;
    panel.scrollTop = 0;
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-gg-mnav-open]');
    if (opener) {
      var target = document.getElementById(opener.getAttribute('data-gg-mnav-open'));
      if (!target) return;
      event.preventDefault();
      opener.setAttribute('aria-expanded', 'true');
      mnavActivate(target, opener.closest('[data-gg-mnav-panel]'), false);
      var backBtn = target.querySelector('[data-gg-mnav-back]');
      if (backBtn) backBtn.focus({ preventScroll: true });
      return;
    }
    var back = event.target.closest('[data-gg-mnav-back]');
    if (back) {
      event.preventDefault();
      var current = back.closest('[data-gg-mnav-panel]');
      var parent = document.getElementById(current.getAttribute('data-parent'));
      mnavActivate(parent, current, true);
      var trigger = parent && parent.querySelector('[aria-controls="' + current.id + '"]');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus({ preventScroll: true });
      }
    }
  });

  document.addEventListener('gg:drawer:open', function (event) {
    var nav = event.detail && event.detail.drawer && event.detail.drawer.querySelector('[data-gg-mnav]');
    if (!nav) return;
    nav.querySelectorAll('[data-gg-mnav-panel]').forEach(function (panel) {
      var root = panel.id === 'gg-mnav-root';
      panel.classList.toggle('is-active', root);
      panel.classList.remove('is-behind');
      panel.inert = !root;
    });
    nav.querySelectorAll('[data-gg-mnav-open]').forEach(function (el) { el.setAttribute('aria-expanded', 'false'); });
  });

  /* ---------------------------------------------------------------------
     Sticky header shadow/blur state
     --------------------------------------------------------------------- */
  var header = document.querySelector('[data-gg-header]');
  if (header) {
    var onScroll = function () {
      header.classList.toggle('is-stuck', window.scrollY > 4);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    // --gg-header-stick: the header's height while it is sticky on this
    // viewport, else 0. Sticky sidebars and toolbars sit just below it.
    var stickHost = header.closest('.gg-header-section') || header;
    var condensed = false;
    var clipPx = 0;
    var publishStick = function () {
      var sticky = getComputedStyle(stickHost).position === 'sticky';
      var visible = stickHost.offsetHeight - (condensed ? clipPx : 0);
      document.documentElement.style.setProperty('--gg-header-stick', (sticky ? visible : 0) + 'px');
    };
    publishStick();
    window.addEventListener('resize', publishStick);
    if (window.ResizeObserver) new ResizeObserver(publishStick).observe(stickHost);

    /* Condense: scrolling down clips the menu row off the stuck header,
       scrolling up (or hovering / focusing the header) brings it back. The
       clip keeps the header's layout height, so the page never jumps; a
       full-width line (CSS ::after) marks the new bottom edge. */
    var navrow = header.querySelector('[data-gg-navrow]');
    if (navrow && header.hasAttribute('data-gg-condense')) {
      var lastY = window.scrollY;
      var hovering = false;
      var clipTimer;
      var canCondense = function () {
        return getComputedStyle(stickHost).position === 'sticky' &&
          navrow.offsetHeight > 0 &&
          !hovering &&
          !stickHost.contains(document.activeElement);
      };
      var setCondensed = function (on) {
        if (on === condensed) return;
        condensed = on;
        clearTimeout(clipTimer);
        if (on) {
          var host = stickHost.getBoundingClientRect();
          clipPx = Math.max(0, Math.round(host.bottom - navrow.getBoundingClientRect().top));
          stickHost.style.setProperty('--gg-header-clip', clipPx + 'px');
          stickHost.style.clipPath = 'inset(0 0 0 0)';
          void stickHost.offsetWidth; // start the transition from "nothing clipped"
          stickHost.classList.add('is-condensed');
          stickHost.style.clipPath = 'inset(0 0 ' + clipPx + 'px 0)';
        } else {
          stickHost.classList.remove('is-condensed');
          stickHost.style.clipPath = 'inset(0 0 0 0)';
          // Drop the clip once open again, so the mega menu and search
          // results can hang below the header.
          clipTimer = setTimeout(function () { stickHost.style.clipPath = ''; }, 260);
        }
        publishStick();
      };
      window.addEventListener('scroll', function () {
        var y = window.scrollY;
        var dy = y - lastY;
        if (y < stickHost.offsetHeight + 40) {
          setCondensed(false);
        } else if (dy > 8 && canCondense()) {
          setCondensed(true);
        } else if (dy < -8) {
          setCondensed(false);
        }
        if (Math.abs(dy) > 8) lastY = y;
      }, { passive: true });
      stickHost.addEventListener('mouseenter', function () { hovering = true; setCondensed(false); });
      stickHost.addEventListener('mouseleave', function () { hovering = false; });
      stickHost.addEventListener('focusin', function () { setCondensed(false); });
    }
  }

  /* ---------------------------------------------------------------------
     Countdowns — [data-gg-countdown="<ISO 8601>"] with
     [data-unit="days|hours|minutes|seconds"] descendants.
     Hides its closest [data-gg-countdown-root] once expired.
     --------------------------------------------------------------------- */
  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function tickCountdown(el) {
    var target = new Date(el.getAttribute('data-gg-countdown')).getTime();
    if (isNaN(target)) return true;
    var diff = target - Date.now();
    if (diff <= 0) {
      var root = el.closest('[data-gg-countdown-root]') || el;
      root.hidden = true;
      return true;
    }
    var s = Math.floor(diff / 1000);
    var values = {
      days: Math.floor(s / 86400),
      hours: Math.floor((s % 86400) / 3600),
      minutes: Math.floor((s % 3600) / 60),
      seconds: s % 60
    };
    el.querySelectorAll('[data-unit]').forEach(function (node) {
      var unit = node.getAttribute('data-unit');
      if (values[unit] !== undefined) node.textContent = pad(values[unit]);
    });
    return false;
  }

  var countdowns = document.querySelectorAll('[data-gg-countdown]');
  if (countdowns.length) {
    var run = function () {
      countdowns.forEach(function (el) { tickCountdown(el); });
    };
    run();
    setInterval(run, 1000);
  }

  /* ---------------------------------------------------------------------
     Long-copy clamp — only engages when the content actually overflows,
     so short descriptions never get a pointless "Lees verder".
     --------------------------------------------------------------------- */
  document.querySelectorAll('[data-gg-clamp]').forEach(function (box) {
    var btn = box.parentElement.querySelector('[data-gg-clamp-btn]');
    if (!btn) return;
    var more = btn.querySelector('[data-gg-clamp-more]');
    var less = btn.querySelector('[data-gg-clamp-less]');

    function measure() {
      box.classList.add('is-clamped');
      var overflows = box.scrollHeight > box.clientHeight + 4;
      btn.hidden = !overflows;
      if (!overflows) box.classList.remove('is-clamped');
    }

    btn.addEventListener('click', function () {
      var clamped = box.classList.toggle('is-clamped');
      btn.setAttribute('aria-expanded', String(!clamped));
      if (more) more.hidden = !clamped;
      if (less) less.hidden = clamped;
    });

    // Images change the height after they load.
    measure();
    box.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', measure, { once: true });
    });
  });

  /* ---------------------------------------------------------------------
     Shipping cut-off — "3 h 12 m" (units from the locale) until the daily cut-off time.
     [data-gg-cutoff] carries data-time="16:00", data-weekdays="1,2,3,4,5" (shipping
     days), data-delivery-days="1,2,3,4,5,6" and data-timezone="Europe/Amsterdam".
     The clock runs in the shop's time zone, not the visitor's.
     Ordered before the cut-off on a shipping day, it ships today; otherwise on the
     next shipping day. Delivery is the first delivery day after it ships, so on a
     weekday: before 16:00 = tomorrow, after 16:00 = the day after. Every
     [data-gg-delivery-day] inside gets "tomorrow" or a weekday name.
     --------------------------------------------------------------------- */
  var WEEKDAYS = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

  function dayList(value, fallback) {
    return (value || fallback).split(',').map(function (d) { return parseInt(d, 10); });
  }

  // Wall-clock date and time in the given zone; falls back to the visitor's clock.
  function zonedNow(timeZone) {
    var now = new Date();
    try {
      var parts = {};
      new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone, hourCycle: 'h23', weekday: 'short',
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', second: 'numeric'
      }).formatToParts(now).forEach(function (p) { parts[p.type] = p.value; });
      return {
        y: +parts.year, mo: +parts.month, d: +parts.day, weekday: WEEKDAYS[parts.weekday],
        secs: (+parts.hour % 24) * 3600 + +parts.minute * 60 + +parts.second
      };
    } catch (e) {
      return {
        y: now.getFullYear(), mo: now.getMonth() + 1, d: now.getDate(),
        weekday: now.getDay() === 0 ? 7 : now.getDay(),
        secs: now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()
      };
    }
  }

  function dayLabel(clock, offset, lang) {
    try {
      if (offset === 1) return new Intl.RelativeTimeFormat(lang, { numeric: 'auto' }).format(1, 'day');
      // Noon UTC on the target calendar date, so DST never shifts the day.
      var date = new Date(Date.UTC(clock.y, clock.mo - 1, clock.d + offset, 12));
      return new Intl.DateTimeFormat(lang, { weekday: 'long', timeZone: 'UTC' }).format(date);
    } catch (e) {
      return '';
    }
  }

  function updateCutoffs() {
    var lang = document.documentElement.lang || 'en';
    document.querySelectorAll('[data-gg-cutoff]').forEach(function (el) {
      var time = (el.getAttribute('data-time') || '16:00').split(':');
      var shipDays = dayList(el.getAttribute('data-weekdays'), '1,2,3,4,5');
      var deliveryDays = dayList(el.getAttribute('data-delivery-days'), '1,2,3,4,5,6');
      var clock = zonedNow(el.getAttribute('data-timezone') || 'Europe/Amsterdam');
      var cutoffSecs = parseInt(time[0], 10) * 3600 + parseInt(time[1] || '0', 10) * 60;
      var weekdayAt = function (offset) { return ((clock.weekday - 1 + offset) % 7) + 1; };

      var open = shipDays.indexOf(clock.weekday) !== -1 && clock.secs < cutoffSecs;
      el.classList.toggle('is-closed', !open);

      var shipOffset = 0;
      if (!open) {
        shipOffset = 1;
        while (shipOffset < 14 && shipDays.indexOf(weekdayAt(shipOffset)) === -1) shipOffset++;
      }
      var deliveryOffset = shipOffset + 1;
      while (deliveryOffset < 21 && deliveryDays.indexOf(weekdayAt(deliveryOffset)) === -1) deliveryOffset++;
      var label = dayLabel(clock, deliveryOffset, lang);
      el.querySelectorAll('[data-gg-delivery-day]').forEach(function (day) { day.textContent = label; });

      var remain = el.querySelector('[data-gg-cutoff-remaining]');
      if (!remain || !open) return;
      var diff = cutoffSecs - clock.secs;
      var h = Math.floor(diff / 3600);
      var m = Math.floor((diff % 3600) / 60);
      // Unit letters come from the locale (gg.promo.unit_hours / unit_minutes).
      var uh = remain.getAttribute('data-unit-h') || 'h';
      var um = remain.getAttribute('data-unit-m') || 'm';
      remain.textContent = h > 0 ? h + ' ' + uh + ' ' + m + ' ' + um : m + ' ' + um;
    });
  }

  /* ---------------------------------------------------------------------
     Carousel — [data-gg-carousel] is a horizontal scroller. On desktop
     (>= 1024px) its [data-gg-carousel-prev/-next] buttons move it one item
     and loop at the ends; data-autoplay="5" moves it every 5 s while it is in
     view, not hovered or focused, and the visitor hasn't asked for reduced
     motion. Phones keep plain swiping. Buttons hide when nothing overflows.
     --------------------------------------------------------------------- */
  document.querySelectorAll('[data-gg-carousel]').forEach(function (list) {
    var scope = list.closest('section') || document;
    var nav = scope.querySelector('[data-gg-carousel-nav]');
    var desktop = window.matchMedia('(min-width: 1024px)');
    var calm = window.matchMedia('(prefers-reduced-motion: reduce)');
    var seconds = parseFloat(list.getAttribute('data-autoplay')) || 0;
    var timer = null;
    var inView = false;
    var paused = false;

    var step = function () {
      var first = list.firstElementChild;
      if (!first) return list.clientWidth;
      var gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    };
    var overflows = function () { return list.scrollWidth - list.clientWidth > 4; };
    var move = function (dir) {
      var max = list.scrollWidth - list.clientWidth;
      if (dir > 0 && list.scrollLeft >= max - 4) list.scrollTo({ left: 0 });
      else if (dir < 0 && list.scrollLeft <= 4) list.scrollTo({ left: max });
      else list.scrollBy({ left: dir * step() });
    };
    var stop = function () { clearInterval(timer); timer = null; };
    var start = function () {
      stop();
      if (!seconds || !desktop.matches || calm.matches || !inView || paused || !overflows()) return;
      timer = setInterval(function () { move(1); }, seconds * 1000);
    };
    var sync = function () {
      if (nav) nav.hidden = !(desktop.matches && overflows());
      start();
    };

    if (nav) {
      nav.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-gg-carousel-prev], [data-gg-carousel-next]');
        if (!btn) return;
        move(btn.hasAttribute('data-gg-carousel-next') ? 1 : -1);
        start(); // a fresh interval after the visitor's own move
      });
    }
    [list, nav].forEach(function (el) {
      if (!el) return;
      el.addEventListener('mouseenter', function () { paused = true; stop(); });
      el.addEventListener('mouseleave', function () { paused = false; start(); });
      el.addEventListener('focusin', function () { paused = true; stop(); });
      el.addEventListener('focusout', function () { paused = false; start(); });
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        start();
      }, { threshold: 0.3 }).observe(list);
    } else {
      inView = true;
    }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });
    if (desktop.addEventListener) desktop.addEventListener('change', sync);
    window.addEventListener('resize', sync);
    sync();
  });

  /* ---------------------------------------------------------------------
     Sideways row ([data-gg-hscroll]): marks .is-overflowing / .is-end for the
     edge fade and shows the prev/next buttons (mouse users) when it overflows.
     --------------------------------------------------------------------- */
  document.querySelectorAll('[data-gg-hscroll]').forEach(function (wrap) {
    var track = wrap.querySelector('[data-gg-hscroll-track]');
    var prev = wrap.querySelector('[data-gg-hscroll-prev]');
    var next = wrap.querySelector('[data-gg-hscroll-next]');
    if (!track) return;
    var update = function () {
      var max = track.scrollWidth - track.clientWidth;
      var over = max > 4;
      wrap.classList.toggle('is-overflowing', over);
      wrap.classList.toggle('is-end', !over || track.scrollLeft >= max - 4);
      if (prev) prev.hidden = !over || track.scrollLeft <= 4;
      if (next) next.hidden = !over || track.scrollLeft >= max - 4;
    };
    var page = function (dir) { track.scrollBy({ left: dir * track.clientWidth * 0.7 }); };
    if (prev) prev.addEventListener('click', function () { page(-1); });
    if (next) next.addEventListener('click', function () { page(1); });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    // Start with the current subcategory in view.
    var active = track.querySelector('[aria-current]');
    if (active && active.offsetLeft + active.offsetWidth > track.clientWidth) {
      track.scrollLeft = active.offsetLeft - 16;
    }
    update();
  });

  /* ---------------------------------------------------------------------
     Homepage scroll reveal (html.gg-reveal-on, set in theme.liquid): each
     section after the first fades up once as it enters the view.
     --------------------------------------------------------------------- */
  if (document.documentElement.classList.contains('gg-reveal-on')) {
    window.ggRevealReady = true;
    var revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        revealIO.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    document.querySelectorAll('#MainContent > .shopify-section:not(:first-child)').forEach(function (el) {
      revealIO.observe(el);
    });
  }

  /* ---------------------------------------------------------------------
     Free shipping in another currency (snippets/gg-freeship): the threshold
     is in euro cents; Shopify.currency.rate converts it to the visitor's
     currency, then the remaining amount and the bar are filled in.
     --------------------------------------------------------------------- */
  window.ggFreeship = function (root) {
    var cur = window.Shopify && window.Shopify.currency;
    var rate = cur ? parseFloat(cur.rate) : NaN;
    (root || document).querySelectorAll('[data-gg-freeship][data-exact="false"]').forEach(function (el) {
      if (!rate || rate <= 0) return;
      var threshold = Math.ceil(parseInt(el.getAttribute('data-threshold'), 10) * rate);
      var total = parseInt(el.getAttribute('data-total'), 10) || 0;
      var remaining = threshold - total;
      var todo = el.querySelector('[data-gg-freeship-todo]');
      var done = el.querySelector('[data-gg-freeship-done]');
      var fill = el.querySelector('[data-gg-freeship-fill]');
      if (fill) {
        var pct = Math.max(0, Math.min(100, Math.floor(total * 100 / threshold)));
        fill.style.width = pct + '%';
        if (fill.parentElement) fill.parentElement.setAttribute('aria-valuenow', pct);
      }
      if (remaining <= 0) {
        if (todo) todo.hidden = true;
        if (done) done.hidden = false;
        return;
      }
      var amount;
      try {
        amount = new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency', currency: el.getAttribute('data-currency') || cur.active,
          currencyDisplay: 'narrowSymbol'
        }).format(remaining / 100);
      } catch (e) {
        amount = (remaining / 100).toFixed(2) + ' ' + (el.getAttribute('data-currency') || '');
      }
      if (todo) {
        todo.textContent = (el.getAttribute('data-template') || '').replace('[amount]', amount);
        todo.hidden = false;
      }
      if (done) done.hidden = true;
    });
  };
  window.ggFreeship(document);

  // Exposed so the cart drawer can re-evaluate after a Section Rendering refresh.
  window.ggUpdateCutoffs = updateCutoffs;
  updateCutoffs();
  setInterval(updateCutoffs, 30000);
})();

/* ---------------------------------------------------------------------------
   Predictive search. Shopify renders the panel for us through /search/suggest
   with section_id, so nothing about the results is built in JavaScript.
--------------------------------------------------------------------------- */
(function () {
  var wrap = document.querySelector('[data-gg-search]');
  if (!wrap) return;
  var input = wrap.querySelector('[data-gg-search-input]');
  var panel = wrap.querySelector('[data-gg-search-panel]');
  if (!input || !panel) return;

  var limit = parseInt(panel.getAttribute('data-gg-search-limit'), 10) || 5;
  var timer = null;
  var controller = null;
  var lastTerm = '';

  function close() {
    panel.hidden = true;
    panel.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
    lastTerm = '';
  }

  function items() {
    return Array.prototype.slice.call(panel.querySelectorAll('[data-gg-psearch-item]'));
  }

  function move(step) {
    var list = items();
    if (!list.length) return;
    var current = list.findIndex(function (el) { return el.classList.contains('is-active'); });
    var next = current + step;
    if (next < 0) next = list.length - 1;
    if (next >= list.length) next = 0;
    list.forEach(function (el) { el.classList.remove('is-active'); });
    list[next].classList.add('is-active');
    list[next].scrollIntoView({ block: 'nearest' });
  }

  function search(term) {
    if (controller) controller.abort();
    controller = new AbortController();

    var url = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'search/suggest'
      + '?q=' + encodeURIComponent(term)
      + '&resources[type]=product,collection,article,page'
      + '&resources[limit]=' + limit
      + '&resources[options][unavailable_products]=last'
      + '&section_id=gg-predictive-search';

    fetch(url, { signal: controller.signal })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var results = parsed.querySelector('[data-gg-psearch-results]');
        if (!results) { close(); return; }
        panel.innerHTML = results.outerHTML;
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      })
      .catch(function () { /* aborted or offline — leave the last panel alone */ });
  }

  input.addEventListener('input', function () {
    var term = input.value.trim();
    if (timer) clearTimeout(timer);
    if (term.length < 2) { close(); return; }
    if (term === lastTerm) return;
    lastTerm = term;
    timer = setTimeout(function () { search(term); }, 220);
  });

  input.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') { close(); return; }
    if (panel.hidden) return;
    if (event.key === 'ArrowDown') { event.preventDefault(); move(1); }
    if (event.key === 'ArrowUp') { event.preventDefault(); move(-1); }
    if (event.key === 'Enter') {
      var active = panel.querySelector('[data-gg-psearch-item].is-active');
      if (active) { event.preventDefault(); window.location.href = active.href; }
    }
  });

  input.addEventListener('focus', function () {
    if (input.value.trim().length >= 2 && panel.innerHTML) panel.hidden = false;
  });

  document.addEventListener('click', function (event) {
    if (!wrap.contains(event.target)) close();
  });
})();

/* Country & language switcher popover (snippets/gg-localization): a native
   <details>, so it works without JS; this only adds close-on-outside-click
   and Escape. */
(function () {
  'use strict';
  var pickers = document.querySelectorAll('[data-gg-loc]');
  if (!pickers.length) return;

  document.addEventListener('click', function (event) {
    pickers.forEach(function (picker) {
      if (picker.open && !picker.contains(event.target)) picker.open = false;
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    pickers.forEach(function (picker) {
      if (!picker.open) return;
      picker.open = false;
      picker.querySelector('summary').focus();
    });
  });
})();
